import { CASES } from "./constants";
import { safeGetJSON } from "tap/utils/safeLocalStorage";
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  getInitialPaginationRange,
  getStoredItemsPerPage,
  PAGINATION_STORAGE_KEYS,
  persistItemsPerPageFromPayload,
  withPreservedPagination,
} from "tap/utils/paginationPreferences.js";

const paginationKeys = {
  itemsPerPage: PAGINATION_STORAGE_KEYS.windows,
};
const initialItemsPerPage = getStoredItemsPerPage(paginationKeys.itemsPerPage);
const initialPaginationRange = getInitialPaginationRange(initialItemsPerPage);

/**
 * Retrieves persisted sort preference from localStorage.
 * @returns {Array} Sort tuple [field, direction], defaults to ['id', 'DESC']
 */
const getInitialWindowsSort = () => {
  return safeGetJSON("windowsSort", ["id", "DESC"]);
};

/**
 * Windows Redux State Shape
 * @typedef {Object} WindowsState
 * @property {Array} windowSequences - List of window sequence records from API
 * @property {Object} selectedWindowSequence - Full window sequence object for viewing/editing
 * @property {Array} windowSequenceHistory - Audit history for selected window sequence
 * @property {Object|null} selectedHistory - Selected history entry
 * @property {boolean} isCreateDrawerOpen - Create drawer visibility
 * @property {boolean} isEditDrawerOpen - Edit drawer visibility
 * @property {boolean} isViewDrawerOpen - View drawer visibility
 * @property {boolean} refreshWindows - Flag to trigger grid data refetch
 * @property {Object} filterForm - Search/filter form values
 * @property {Object} windowSequenceForm - Create/edit form values
 * @property {Array} statusOptions - Window sequence status options
 * @property {Array} userOptions - User options for filtering (from API)
 * @property {Array} transactionalWindowsOptions - Available transactional window options (from API)
 * @property {number} totalPages - Pagination total pages
 * @property {number} totalItems - Total record count
 * @property {Array} windowsRange - Pagination range [start, end]
 * @property {Array} windowsSort - Sort tuple [field, direction]
 */
const initialState = {
  windowSequences: [],
  selectedWindowSequence: {},
  windowSequenceHistory: [],
  selectedHistory: null,
  isCreateDrawerOpen: false,
  isEditDrawerOpen: false,
  isViewDrawerOpen: false,
  refreshWindows: false,
  filterForm: {
    windowId: "",
    windowSequenceName: "",
    users: "",
    status: "",
  },
  windowSequenceForm: {
    windowSequenceName: "",
    windows: [],
    status: "ACTIVE",
  },
  statusOptions: [
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
    { value: "NEEDS_ATTENTION", label: "Needs Attention" },
  ],
  userOptions: [],
  transactionalWindowsOptions: [],
  totalPages: 0,
  totalItems: 0,
  currentPage: 0,
  windowsRange: initialPaginationRange,
  windowsSort: getInitialWindowsSort(),
  defaultFilterRange: initialPaginationRange,
  itemsPerPage: initialItemsPerPage,
  pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
};

export function windows(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case CASES.UPDATE_WINDOWS: {
      // Persist sort preference to localStorage for session continuity
      if (payload.windowsSort) {
        localStorage.setItem(
          "windowsSort",
          JSON.stringify(payload.windowsSort),
        );
      }
      persistItemsPerPageFromPayload(payload, paginationKeys);
      return { ...state, ...payload };
    }
    case CASES.UPDATE_WINDOWS_SEARCH_FORM: {
      const { data, key } = payload;
      return { ...state, [key]: { ...state[key], ...data } };
    }
    case CASES.UPDATE_WINDOWS_FORM: {
      const { data, key } = payload;
      return { ...state, [key]: { ...state[key], ...data } };
    }
    case CASES.CLEAR_WINDOWS: {
      return withPreservedPagination(
        state,
        {
          ...initialState,
          windowsSort: state.windowsSort,
          userOptions: state.userOptions,
          statusOptions: state.statusOptions,
          transactionalWindowsOptions: state.transactionalWindowsOptions,
        },
        ["windowsRange"],
      );
    }
    case CASES.CLEAR_WINDOW_FORM: {
      return {
        ...state,
        windowSequenceForm: initialState.windowSequenceForm,
        selectedWindowSequence: initialState.selectedWindowSequence,
        windowSequenceHistory: initialState.windowSequenceHistory,
        selectedHistory: initialState.selectedHistory,
      };
    }
    default:
      return state;
  }
}
